/**
 * @fileoverview Landing page navigation bar component.
 * Fixed navbar with glass effect, logo, nav links, and CTAs.
 * @module features/landing/components/LandingNavbar
 */

import { ReactElement } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Languages, LogIn } from 'lucide-react'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { LoginDialog } from './LoginDialog'

interface NavLinkProps {
  href: string
  children: React.ReactNode
}

function NavLink({ href, children }: NavLinkProps): ReactElement {
  return (
    <a
      href={href}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {children}
    </a>
  )
}

/** Language options with flags */
const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
] as const

/**
 * Landing page navigation bar.
 * Features glass morphism effect, responsive design, and smooth hover states.
 *
 * @component
 */
export function LandingNavbar(): ReactElement {
  const { t, i18n } = useTranslation('features-landing')
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const currentLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en'

  const handleLanguageChange = (languageCode: string): void => {
    if (!currentLanguage.startsWith(languageCode)) {
      // Navigate to the language-specific URL (LandingPage will set i18n)
      navigate(`/${languageCode}/`)
    }
  }

  const handleDropdownOpenChange = (open: boolean): void => {
    // Remove focus from trigger when dropdown closes
    // Use setTimeout to let Radix finish returning focus to trigger first
    if (!open) {
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      }, 0)
    }
  }

  return (
    <nav
      className="fixed top-0 w-full z-50 border-b"
      style={{
        backdropFilter: 'blur(24px)',
        borderColor: 'oklch(1 0 0 / 0.1)',
      }}
    >
      <div className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/">
          <img
            src="/abyssblock-white.webp"
            alt="AbyssBlock"
            className="h-9 w-auto"
          />
        </Link>

        {/* Navigation Links - Hidden on mobile */}
        <div className="hidden md:flex items-center gap-8 text-sm font-light">
          <NavLink href="#integrations">{t('navbar.integrations')}</NavLink>
          <NavLink href="#features">{t('navbar.features')}</NavLink>
        </div>

        {/* CTAs */}
        <div className="flex items-center gap-4">
          {/* Language Dropdown */}
          <DropdownMenu onOpenChange={handleDropdownOpenChange}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none"
              >
                <Languages className="h-4 w-4" />
                <span className="font-medium">
                  {currentLanguage.startsWith('pl') ? 'PL' : 'EN'}
                </span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-32">
              {LANGUAGE_OPTIONS.map((option) => {
                const isActive = currentLanguage.startsWith(option.code)
                return (
                  <DropdownMenuItem
                    key={option.code}
                    onClick={() => handleLanguageChange(option.code)}
                    className={cn(
                      'flex items-center gap-2 cursor-pointer',
                      isActive && 'bg-accent'
                    )}
                  >
                    <span className="text-base">{option.flag}</span>
                    <span>{option.label}</span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <LoginDialog>
            <button
              type="button"
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
            >
              {isMobile ? (
                <LogIn className="h-5 w-5" />
              ) : (
                t('navbar.login')
              )}
            </button>
          </LoginDialog>
          <a
            href="#early-access"
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-semibold',
              'bg-primary text-primary-foreground',
              'hover:brightness-95 transition-all',
              'shadow-lg btn-glow-white'
            )}
          >
            {isMobile ? t('navbar.applyBetaShort') : t('navbar.applyBeta')}
          </a>
        </div>
      </div>
    </nav>
  )
}
